import { BulbOutlined, CaretRightOutlined, DownCircleOutlined, RightCircleOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Button, DatePicker, Divider, Form, FormInstance, Input, InputNumber, Modal, Tooltip, Typography } from 'antd'
import cls from 'classnames'
import { isNumber } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { MultipleUserSearch } from '@/components/multiple-user-search/multiple-user-search.component'
import {
  GetMonthlyEventsDocument,
  GetTimelinePostsDocument,
  PostType,
  RequestedPostFragment,
  SectionType,
  useGetGroupQuery,
  useGetViewerQuery,
  usePostCreateMutation,
  usePostUpdateMutation,
} from '@/generated/graphql'
import { PostEditorSelectorList } from '@/modules/post/post-editor/post-editor-selector.component'
import { emptyEventState } from '@/modules/post/post-editor/post-editor.state'

import s from './event-post-modal.module.less'

interface EventPostComponentProps {
  post?: RequestedPostFragment
  form: FormInstance
}

export const EventPostComponent: React.FC<EventPostComponentProps> = ({ post, form }) => {
  const { t } = useTranslation()
  const hasSignSectionValue =
    isNumber(post?.event?.openPosition) || !!post?.event?.deadline || !!post?.event?.restriction
  const hasMoreSectionValue = !!post?.event?.avcLink || !!post?.event?.avcPassword || !!post?.event?.avcRoom
  const [signSectionVisible, { toggle: toggleSignSectionVisible }] = useToggle(hasSignSectionValue)
  const [moreSectionVisible, { toggle: toggleMoreSectionVisible }] = useToggle(hasMoreSectionValue)

  const initialValues = post
    ? {
        topic: post.event!.topic,
        content: post.event!.content,
        location: post.event!.location,
        openPosition: post.event!.openPosition,
        restriction: post.event!.restriction,
        deadline: post.event!.deadline ? moment(post.event!.deadline) : null,
        guests: post.mentions.map((mention) => mention.user),
        foreignGuests: post.event!.foreignGuests,
        avcRoom: post.event!.avcRoom,
        avcPassword: post.event!.avcPassword,
        avcLink: post.event!.avcLink,
        startTime: moment(post.event!.startTime),
        endTime: moment(post.event!.endTime),
      }
    : emptyEventState

  return (
    <div className={s.form} style={{ maxHeight: Math.round(document.body.clientHeight * 0.7), overflow: 'auto' }}>
      <Form
        form={form}
        preserve={false}
        labelCol={{ span: 5 }}
        wrapperCol={{ offset: 1 }}
        layout='horizontal'
        initialValues={initialValues}
        validateMessages={{ required: t('post.modal.createValidation') }}
        requiredMark={!post}
      >
        <Typography.Title level={5}>{t('post.event.basics')}</Typography.Title>
        <Divider />
        <Form.Item name='topic' label={t('post.event.topic')} rules={[{ required: true }]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name='content' label={t('post.event.content')} rules={[{ required: true }]}>
          <Input.TextArea />
        </Form.Item>
        <Form.Item name='location' label={t('post.event.location')} rules={[{ required: true }]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name='startTime' label={t('post.event.startTime')} rules={[{ required: true }]}>
          <DatePicker
            onChange={(value) => {
              value && form.setFieldsValue({ endTime: value?.clone().add(1, 'h') })
            }}
            showNow={false}
            format='YYYY-MM-DD HH:mm'
            showTime={{ minuteStep: 5 }}
          />
        </Form.Item>
        <Form.Item
          name='endTime'
          label={t('post.event.endTime')}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value: moment.Moment) {
                if (value.isBefore(getFieldValue('startTime'))) {
                  return Promise.reject(new Error('结束时间不能早于开始时间'))
                }
                return Promise.resolve()
              },
            }),
          ]}
          dependencies={['startTime']}
        >
          <DatePicker showNow={false} format='YYYY-MM-DD HH:mm' showTime={{ minuteStep: 5 }} />
        </Form.Item>
        <Form.Item name='guests' label={t('post.event.guests')}>
          <MultipleUserSearch />
        </Form.Item>
        <Form.Item name='foreignGuests' label={t('post.event.foreignGuests')}>
          <Input allowClear />
        </Form.Item>
        <Typography.Title onClick={() => toggleSignSectionVisible()} className={s.optional} level={5}>
          {t('post.event.signUp')}{' '}
          {signSectionVisible ? <DownCircleOutlined className={s.icon} /> : <RightCircleOutlined className={s.icon} />}
        </Typography.Title>
        <Divider />
        <Form.Item name='restriction' label={t('post.event.restriction')} hidden={!signSectionVisible}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name='deadline' label={t('post.event.deadline')} hidden={!signSectionVisible}>
          <DatePicker format='YYYY-MM-DD HH:mm' showTime={{ minuteStep: 5 }} />
        </Form.Item>
        <Form.Item
          name='openPosition'
          label={t('post.event.openPosition')}
          rules={[{ type: 'number', min: 0 }]}
          hidden={!signSectionVisible}
        >
          <InputNumber />
        </Form.Item>
        <Typography.Title onClick={() => toggleMoreSectionVisible()} className={s.optional} level={5}>
          {t('post.event.moreInfo')}{' '}
          {moreSectionVisible ? <DownCircleOutlined className={s.icon} /> : <RightCircleOutlined className={s.icon} />}
        </Typography.Title>
        <Divider />
        <Form.Item name='avcRoom' label={t('post.event.avcRoom')} hidden={!moreSectionVisible}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name='avcPassword' label={t('post.event.avcPassword')} hidden={!moreSectionVisible}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name='avcLink' label={t('post.event.avcLink')} hidden={!moreSectionVisible}>
          <Input allowClear />
        </Form.Item>
      </Form>
    </div>
  )
}

export const EventPostModal: React.FC<{ post?: RequestedPostFragment; groupId?: number; sectionId?: SectionType }> = ({
  post,
  groupId: passedInGroupId,
  sectionId,
  children,
  ...props
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const [visible, { toggle }] = useToggle()
  const [groupSelectorVisible, { toggle: toggleGroupSelectorVisible }] = useToggle()
  const [groupId, setGroupId] = React.useState<number | undefined>(post ? post.group?.groupId : passedInGroupId || 1)

  const { data } = useGetViewerQuery()

  const { data: groupData } = useGetGroupQuery({
    fetchPolicy: 'cache-first',
    skip: !groupId,
    variables: {
      groupId: groupId!,
      withAdmins: true,
      pagination: {
        first: 1000,
      },
    },
  })

  const [postCreate, { loading: createLoading }] = usePostCreateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!, getOperationName(GetMonthlyEventsDocument)!],
  })

  const [postUpdate, { loading: updateLoading }] = usePostUpdateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
  })

  const viewer = data!.viewer
  const group = groupData?.group
  const showGroupSelector = !post

  const onOk = async () => {
    const values = await form.validateFields()
    const eventInfo = {
      content: values.content,
      topic: values.topic,
      openPosition: values.openPosition,
      restriction: values.restriction,
      deadline: values.deadline,
      location: values.location,
      startTime: values.startTime,
      guests: values.guests.map((guest: { email: string }) => guest.email),
      foreignGuests: values.foreignGuests,
      endTime: values.endTime,
      avcRoom: values.avcRoom,
      avcPassword: values.avcPassword,
      avcLink: values.avcLink,
    }

    if (post) {
      await postUpdate({
        variables: {
          postId: post.postId,
          post: { event: eventInfo, content: values.content },
        },
      })
      toggle()
    } else {
      await postCreate({
        variables: {
          post: { event: eventInfo, content: values.content, postType: PostType.Event, groupId, sectionId },
        },
      })
      toggle()
    }
  }

  children = React.cloneElement(children as any, {
    ...props,
    onClick: toggle,
  })

  const footer = (
    <div className={s.footer}>
      <Button loading={createLoading || updateLoading} type='primary' onClick={() => onOk()}>
        {post ? t('post.editor.save') : t('post.editor.publishTo')}
      </Button>
      {showGroupSelector && (
        <span className={s.groupSelector} onClick={() => toggleGroupSelectorVisible()}>
          {group ? group.name : t('post.editor.myTimeline')}
        </span>
      )}
    </div>
  )

  const groupSelectorHeader = (
    <Typography.Title level={4}>
      {t('post.editor.choosePublishGroup')}
      <Tooltip title={t('impact.tip.publish')}>
        <BulbOutlined style={{ color: '#FFBB5C', marginLeft: 8, fontSize: 16 }} />
      </Tooltip>
    </Typography.Title>
  )

  const groupSelector = (
    <Modal
      key='groupSelector'
      visible={groupSelectorVisible}
      footer={false}
      className={s.modal}
      onCancel={() => toggleGroupSelectorVisible()}
      title={groupSelectorHeader}
      centered
    >
      <PostEditorSelectorList
        postType={post?.postType}
        setGroupId={setGroupId}
        onSelect={() => toggleGroupSelectorVisible()}
        viewer={viewer}
      />
    </Modal>
  )

  return (
    <>
      {children}
      {groupSelector}
      <Modal
        key='event'
        visible={visible}
        footer={footer}
        className={s.modal}
        style={{ top: 10 }}
        centered
        destroyOnClose
        onCancel={() => toggle()}
        title={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {post ? t('post.event.edit') : t('post.event.title')}
            {sectionId && (
              <>
                <CaretRightOutlined style={{ fontSize: 14, color: '#6A6A6A', marginRight: 4, marginLeft: 4 }} />
                <span
                  className={cls(
                    s.header,
                    sectionId === SectionType.NewAgoran && s.section1Color,
                    sectionId === SectionType.Entertainment && s.section2Color,
                    sectionId === SectionType.Synthesis && s.section3Color
                  )}
                >
                  {sectionId && t(`section.${sectionId}.title`)}
                </span>
              </>
            )}
          </div>
        }
      >
        <EventPostComponent post={post} form={form} />
      </Modal>
    </>
  )
}
